import { NgModule, Injectable } from '@angular/core';
import { Ng2StateDeclaration, UIRouterModule } from '@uirouter/angular';
import { StateService, Transition } from '@uirouter/core';
import { SessionService } from '@monsido/core/session/session.service';
import { MonUIRouterTransitionService } from '@monsido/ng2/services/ui-router-transition-service/ui-router-transition-service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';

import { StatisticsSummaryComponent } from '@client/app/feature-modules/site-analytics/components/statistics-summary/statistics-summary.component';
import { StatisticsVisitorsLogComponent } from './visitors/log/log.component';
import { StatisticsPageScaffoldComponent } from '@client/app/feature-modules/site-analytics/components/statistics-page-scaffold/statistics-page-scaffold.component';
import { BOUNCE_RATE_PAGES_PAGE_CONFIG } from '@client/app/feature-modules/site-analytics/configs/bounce-rate-pages.config';
import { DOCUMENTS_WITHOUT_DOWNLOADS_PAGE_CONFIG } from '@client/app/feature-modules/site-analytics/configs/documents-without-downloads.config';
import { ENTRY_PAGES_PAGE_CONFIG } from '@client/app/feature-modules/site-analytics/configs/entry-pages.config';
import { EXIT_PAGES_PAGE_CONFIG } from '@client/app/feature-modules/site-analytics/configs/exit-pages.config';
import { LEAST_POPULAR_PAGE_CONFIG } from '@client/app/feature-modules/site-analytics/configs/least-popular-page.config';
import { MOST_DOWNLOADS_PAGE_CONFIG } from '@client/app/feature-modules/site-analytics/configs/most-downloads-page.config';
import { MOST_POPULAR_PAGE_CONFIG } from '@client/app/feature-modules/site-analytics/configs/most-popular-page.config';
import { SITE_SEARCH_PAGE_CONFIG } from '@client/app/feature-modules/site-analytics/configs/site-search.config';
import {
    EVENTS_CATEGORIES_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/events-categories.config';
import {
    EXTERNAL_REFERRING_DOMAINS_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/external-referring-domains-page.config';
import { PAGES_WITHOUT_VISITS_PAGE_CONFIG } from '@client/app/feature-modules/site-analytics/configs/pages-without-visits.config';
import { SEARCH_ENGINES_PAGE_CONFIG } from '@client/app/feature-modules/site-analytics/configs/search-engines-page.config';
import { SOCIAL_NETWORKS_PAGE_CONFIG } from '@client/app/feature-modules/site-analytics/configs/social-networks-page.config';
import {
    VISITORS_BROWSERS_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/visitors-browsers-page.config';
import {
    VISITORS_DEVICES_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/visitors-devices-page.config';
import {
    VISITORS_LANGUAGES_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/visitors-languages-page.config';
import {
    VISITORS_LOCATION_CITIES_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/visitors-location-cities-page.config';
import {
    VISITORS_LOCATION_REGIONS_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/visitors-location-regions-page.config';
import {
    VISITORS_LOCATIONS_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/visitors-locations-page.config';
import {
    VISITORS_OPERATING_SYSTEMS_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/visitors-operating-systems-page.config';
import {
    VISITORS_ORGANIZATIONS_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/visitors-organizations-page.config';
import {
    VISITORS_SCREEN_RESOLUTIONS_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/visitors-screen-resolutions-page.config';
import { SiteAnalyticsScaffoldConfigService } from '@client/app/feature-modules/site-analytics/services/site-analytics-scaffold-config.service';
import type { SiteAnalyticsScaffoldConfig } from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import {
    StatisticsEventsPageComponent,
} from '@client/app/feature-modules/site-analytics/components/statistics-events-page/statistics-events-page.component';
import {
    StatisticsSummaryScaffoldPageComponent,
} from '@client/app/feature-modules/site-analytics/components/statistics-summary-scaffold-page/statistics-summary-scaffold-page.component';
import {
    TRAFFIC_SOURCES_SUMMARY_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/traffic-sources-summary-page.config';
import {
    VISITORS_SUMMARY_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/visitors-summary-page.config';

import { CoreDomainService } from '@client/app/core/services/domain/core-domain.service';
import {
    paginationUrlQueryParamsDeclarations,
} from '@client/app/shared-modules/state/state-params-declarations/pagination-params-declarations';
import {
    paginationUrlQueryParamsValidator,
} from '@client/app/shared-modules/state/state-validators/pagination-url-query-params-validator';

@Injectable({ providedIn: 'root' })
export class StatisticsAccessGuard {
    constructor (
        private sessionService: SessionService,
        private stateService: StateService,
        private monUIRouterTransitionService: MonUIRouterTransitionService,
        private pipeService: PipeService,
        private coreDomainService: CoreDomainService,
    ) {}

    checkAccess (transition: Transition) {
        const statEnabledOnDomain = this.sessionService.domain !== null && this.sessionService.domain.features.statistics;
        const statEnabledOnDomainGroup =
            this.sessionService.domainGroup !== null &&
            this.sessionService.customer !== null &&
            this.sessionService.customer.plan_traits.domain_group_statistics;
        const isDemo = this.pipeService.get('monCustomerHasPlan')('demo');

        if (isDemo) {
            return true;
        } else if (statEnabledOnDomain && this.sessionService.domainGroup === null) {
            return true;
        } else if (statEnabledOnDomain && statEnabledOnDomainGroup) {
            return true;
        } else {
            this.coreDomainService.changeDomain();
            const params = transition.params();
            return this.monUIRouterTransitionService.onTransitionFinished(() => {
                return this.stateService.go('base.customer.domain.dashboard', params) as unknown as Promise<void>;
            });
        }
    }

    checkTooltipVisibility (): boolean {
        if (this.sessionService.domain !== null) {
            return !this.sessionService.domain.monsido_script_installed && !this.pipeService.get('isStatsSampleData')(this.sessionService.domain);
        }
        return false;
    }
}

/**
 * Statistics routing configuration for Angular.
 * This module provides routing for the Statistics section with proper access control.
 * Uses Angular service-based access guard following the established pattern.
 */

const SITE_ANALYTICS_QUERY_PARAMS =
    '?page_size&page&search&sort_by&sort_dir&from&to&compare_from&compare_to&comparison';

interface SiteAnalyticsScaffoldStateOptions {
    stateName: string;
    urlPath: string;
    title: string;
    config: SiteAnalyticsScaffoldConfig;
    additionalParams?: Record<string, unknown>;
}

function createSiteAnalyticsScaffoldState ({
    stateName,
    urlPath,
    title,
    config,
    additionalParams,
}: SiteAnalyticsScaffoldStateOptions): Ng2StateDeclaration {
    const queryParams = SITE_ANALYTICS_QUERY_PARAMS.replace(/^\?/, '');
    const queryDelimiter = urlPath.includes('?') ? '&' : '?';

    return {
        name: stateName,
        url: `${urlPath}${queryDelimiter}${queryParams}`,
        params: {
            ...paginationUrlQueryParamsDeclarations(),
            search: { dynamic: true, value: '' },
            sort_by: { dynamic: true, value: config.defaultSortBy },
            sort_dir: { dynamic: true, value: config.defaultSortDir },
            from: { dynamic: true, value: '' },
            to: { dynamic: true, value: '' },
            compare_from: { dynamic: true, value: '' },
            compare_to: { dynamic: true, value: '' },
            comparison: { dynamic: true, value: 'false' },
            ...(additionalParams || {}),
        },
        resolve: [
            {
                token: 'siteAnalyticsScaffoldConfig',
                deps: [SiteAnalyticsScaffoldConfigService],
                resolveFn: (configService: SiteAnalyticsScaffoldConfigService) =>
                    configService.getConfig(config.dataSource),
            },
        ],
        redirectTo: paginationUrlQueryParamsValidator(stateName),
        views: {
            'content@base.customer.domain': {
                component: StatisticsPageScaffoldComponent,
                bindings: {
                    config: 'siteAnalyticsScaffoldConfig',
                },
            },
        },
        data: {
            title,
            usesModernStyles: true,
            theme: 'modern',
        },
    };
}

const statisticsStates: Ng2StateDeclaration[] = [
    // Access-controlled abstract route
    {
        name: 'base.customer.domain.statistics',
        url: '/statistics',
        abstract: true,
        data: {
            sidebar: 'statistics',
            title: 'Acquia Web Governance - Statistics',
        },
        resolve: [
            {
                token: 'validateDomain',
                deps: [StatisticsAccessGuard, Transition],
                resolveFn: (guard: StatisticsAccessGuard, transition: Transition) =>
                    guard.checkAccess(transition),
            },
        ],
    },

    // Redirect /statistics to /statistics/summary
    {
        name: 'base.customer.domain.statistics.redirect',
        url: '',
        redirectTo: 'base.customer.domain.statistics.summary',
    },

    // Summary routes
    {
        name: 'base.customer.domain.statistics.summary',
        url: '/summary?from&to',
        params: {
            from: {
                dynamic: true,
                value: '',
            },
            to: {
                dynamic: true,
                value: '',
            },
        },
        views: {
            'content@base.customer.domain': {
                component: StatisticsSummaryComponent,
            },
        },
        data: {
            title: 'Acquia Web Governance - Statistics Summary',
            usesModernStyles: true,
            theme: 'modern',
        },
    },

    // Visitors routes
    {
        name: 'base.customer.domain.statistics.visitors',
        url: '/visitors',
        abstract: true,
    },
    {
        name: 'base.customer.domain.statistics.visitors.summary',
        url: '/summary?from&to',
        params: {
            from: {
                dynamic: true,
                value: '',
            },
            to: {
                dynamic: true,
                value: '',
            },
        },
        resolve: [
            {
                token: 'statisticsSummaryScaffoldConfig',
                resolveFn: () => VISITORS_SUMMARY_PAGE_CONFIG,
            },
        ],
        views: {
            'content@base.customer.domain': {
                component: StatisticsSummaryScaffoldPageComponent,
                bindings: {
                    config: 'statisticsSummaryScaffoldConfig',
                },
            },
        },
        data: {
            title: 'Acquia Web Governance - Visitors Summary',
            usesModernStyles: true,
            theme: 'modern',
        },
    },
    {
        name: 'base.customer.domain.statistics.visitors.locations',
        url: '/locations',
        abstract: true,
        data: {
            title: 'Acquia Web Governance - Locations',
        },
    },
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.visitors.locations.index',
        urlPath: '/',
        title: 'Acquia Web Governance - Locations',
        config: VISITORS_LOCATIONS_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.visitors.locations.regions',
        urlPath: '/{countryCode}?countryName',
        title: 'Acquia Web Governance - Locations',
        config: VISITORS_LOCATION_REGIONS_PAGE_CONFIG,
        additionalParams: {
            countryName: {
                dynamic: true,
                value: '',
            },
        },
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.visitors.locations.cities',
        urlPath: '/{countryCode}/{regionCode}?countryName&regionName',
        title: 'Acquia Web Governance - Locations',
        config: VISITORS_LOCATION_CITIES_PAGE_CONFIG,
        additionalParams: {
            countryName: {
                dynamic: true,
                value: '',
            },
            regionName: {
                dynamic: true,
                value: '',
            },
        },
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.visitors.language',
        urlPath: '/language',
        title: 'Acquia Web Governance - Languages',
        config: VISITORS_LANGUAGES_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.visitors.organizations',
        urlPath: '/organizations',
        title: 'Acquia Web Governance - ISPs',
        config: VISITORS_ORGANIZATIONS_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.visitors.devices',
        urlPath: '/devices',
        title: 'Acquia Web Governance - Devices',
        config: VISITORS_DEVICES_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.visitors.browsers',
        urlPath: '/browsers',
        title: 'Acquia Web Governance - Browsers',
        config: VISITORS_BROWSERS_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.visitors.os',
        urlPath: '/os',
        title: 'Acquia Web Governance - Operating Systems',
        config: VISITORS_OPERATING_SYSTEMS_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.visitors.resolutions',
        urlPath: '/resolutions',
        title: 'Acquia Web Governance - Screen Resolutions',
        config: VISITORS_SCREEN_RESOLUTIONS_PAGE_CONFIG,
    }),
    {
        name: 'base.customer.domain.statistics.visitors.log',
        url: '/log',
        views: {
            'content@base.customer.domain': {
                component: StatisticsVisitorsLogComponent,
            },
        },
    },

    // Pages routes
    {
        name: 'base.customer.domain.statistics.pages',
        url: '/pages',
        abstract: true,
    },
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.pages.mostPopular',
        urlPath: '/most-popular',
        title: 'Acquia Web Governance - Most Visited Pages',
        config: MOST_POPULAR_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.pages.leastPopular',
        urlPath: '/least-popular',
        title: 'Acquia Web Governance - Least Visited Pages',
        config: LEAST_POPULAR_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.pages.mostDownloads',
        urlPath: '/most-downloads',
        title: 'Acquia Web Governance - Most Downloads',
        config: MOST_DOWNLOADS_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.pages.withoutVisits',
        urlPath: '/without-visits',
        title: 'Acquia Web Governance - Pages with No Visits',
        config: PAGES_WITHOUT_VISITS_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.pages.documentsWithoutDownloads',
        urlPath: '/documents-without-visits',
        title: 'Acquia Web Governance - Documents with No Downloads',
        config: DOCUMENTS_WITHOUT_DOWNLOADS_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.pages.siteSearch',
        urlPath: '/site-search',
        title: 'Acquia Web Governance - Site Search',
        config: SITE_SEARCH_PAGE_CONFIG,
    }),
    {
        name: 'base.customer.domain.statistics.pages.events',
        url: '/events?page_size&page&search&sort_by&sort_dir&from&to&tab',
        params: {
            ...paginationUrlQueryParamsDeclarations(),
            tab: {
                dynamic: true,
                value: 'categories',
            },
            search: {
                dynamic: true,
                value: '',
            },
            sort_by: {
                dynamic: true,
                value: EVENTS_CATEGORIES_PAGE_CONFIG.defaultSortBy,
            },
            sort_dir: {
                dynamic: true,
                value: EVENTS_CATEGORIES_PAGE_CONFIG.defaultSortDir,
            },
            from: {
                dynamic: true,
                value: '',
            },
            to: {
                dynamic: true,
                value: '',
            },
        },
        redirectTo: paginationUrlQueryParamsValidator('base.customer.domain.statistics.pages.events'),
        views: {
            'content@base.customer.domain': {
                component: StatisticsEventsPageComponent,
            },
        },
        data: {
            title: 'Acquia Web Governance - Events',
            usesModernStyles: true,
            theme: 'modern',
        },
    },

    // Sources routes
    {
        name: 'base.customer.domain.statistics.sources',
        url: '/sources',
        abstract: true,
    },
    {
        name: 'base.customer.domain.statistics.sources.summary',
        url: '/summary?from&to',
        params: {
            from: {
                dynamic: true,
                value: '',
            },
            to: {
                dynamic: true,
                value: '',
            },
        },
        resolve: [
            {
                token: 'statisticsSummaryScaffoldConfig',
                resolveFn: () => TRAFFIC_SOURCES_SUMMARY_PAGE_CONFIG,
            },
        ],
        views: {
            'content@base.customer.domain': {
                component: StatisticsSummaryScaffoldPageComponent,
                bindings: {
                    config: 'statisticsSummaryScaffoldConfig',
                },
            },
        },
        data: {
            title: 'Acquia Web Governance - Traffic Sources Summary',
            usesModernStyles: true,
            theme: 'modern',
        },
    },
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.sources.searchEngines',
        urlPath: '/search-engines',
        title: 'Acquia Web Governance - Search Engines',
        config: SEARCH_ENGINES_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.sources.social',
        urlPath: '/social',
        title: 'Acquia Web Governance - Social Networks',
        config: SOCIAL_NETWORKS_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.sources.domains',
        urlPath: '/domains',
        title: 'Acquia Web Governance - Referring Domains',
        config: EXTERNAL_REFERRING_DOMAINS_PAGE_CONFIG,
    }),

    // Entry/Exit routes
    {
        name: 'base.customer.domain.statistics.entryExit',
        url: '/entry-exit',
        abstract: true,
    },
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.entryExit.entry',
        urlPath: '/entry',
        title: 'Acquia Web Governance - Entry Pages',
        config: ENTRY_PAGES_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.entryExit.exit',
        urlPath: '/exit',
        title: 'Acquia Web Governance - Exit Pages',
        config: EXIT_PAGES_PAGE_CONFIG,
    }),
    createSiteAnalyticsScaffoldState({
        stateName: 'base.customer.domain.statistics.entryExit.bounceRates',
        urlPath: '/bounce-rates',
        title: 'Acquia Web Governance - Bounce Rates',
        config: BOUNCE_RATE_PAGES_PAGE_CONFIG,
    }),
];

@NgModule({
    imports: [UIRouterModule.forChild({ states: statisticsStates })],
    exports: [UIRouterModule],
})
export class StatisticsRoutingModule { }
